import React, { Fragment, useContext, useEffect } from "react";
import useSWR from "swr";
import { useFormik } from "formik";
import { object, string, array } from "yup";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import MaterialTable from "../../../components/materialTable";
import * as PerfilRequest from "../../../services/requests/mi_cyd/perfiles";
import * as PermisoRequest from "../../../services/requests/mi_cyd/permisos";
import { MainContext } from "../../../App";

export default function TabPerfiles(props) {
	const PerfilesSWR = useSWR("perfiles", (key) => PerfilRequest.Obtener(), { revalidateOnFocus: false });
	const PermisosSWR = useSWR("permisos", (key) => PermisoRequest.Obtener(), { revalidateOnFocus: false });
	const { ShowSnackbar } = useContext(MainContext);

	useEffect(() => {
		if (PerfilesSWR.error) {
			ShowSnackbar("Error al intentar obtener los perfiles.", PerfilesSWR.error);
		}
		if (PermisosSWR.error) {
			ShowSnackbar("Error al intentar obtener los permisos.", PermisosSWR.error);
		}
	}, [PerfilesSWR, PermisosSWR]);

	const formik = useFormik({
		initialValues: {
			nombre: "",
			descripcion: "",
			_permisos_ref: [],
		},
		validationSchema: object().shape({
			nombre: string()
				.min(3, "El nombre debe tener al menos ${min} caracteres.")
				.max(50, "El nombre debe tener a los más ${max} caracteres.")
				.required("El nombre es requerido."),
			descripcion: string()
				.min(3, "La descripción debe tener al menos ${min} caracteres.")
				.max(50, "La descripción debe tener a los más ${max} caracteres.")
				.required("La descripción es requerida."),
			_permisos_ref: array()
				.of(string())
				.optional(),
		}),
		onSubmit: (values, helper) => handleAceptar(values),
		enableReinitialize: true,
	});

	/**
	 * Handler para agregar un nuevo perfil.
	 */
	const handleAgregar = () => {
		formik.setFieldValue("tipo", "agregar");
	}

	/**
	 * Handler para actualizar un perfil.
	 * @param {*} event Evento.
	 * @param {*} row Datos.
	 */
	const handleActualizar = (event, row) => {
		formik.setValues(row);
		formik.setFieldValue("tipo", "actualizar");
	}

	/**
	 * Handler para eliminar un perfil.
	 * @param {*} event Evento.
	 * @param {*} row Datos.
	 */
	const handleEliminar = (event, row) => {
		formik.setValues(row);
		formik.setFieldValue("tipo", "eliminar");
	}

	/**
	 * Handler para aceptar la acción.
	 * @param {*} values 
	 */
	const handleAceptar = async (values) => {
		try {
			switch (values.tipo) {
				case "agregar":
					await PerfilRequest.Agregar(values);
					break;
				case "actualizar":
					await PerfilRequest.Actualizar(values);
					break;
				case "eliminar":
					await PerfilRequest.Eliminar(values);
					break;
				default:
					throw new Error("Acción no especificada.")
			}
			ShowSnackbar("Acción realizada exitosamente.");
		} catch (error) {
			ShowSnackbar("Error al intentar realizar la acción.", error);
		} finally {
			handleCancelar();
		}
	}

	const handleCancelar = () => {
		PerfilesSWR.mutate();
		PermisosSWR.mutate();
		formik.resetForm();
	}

	let columns = [
		{
			title: "Nombre",
			field: "nombre",
			defaultSort: "asc",
		}, {
			title: "Descripción",
			field: "descripcion",
		}
	];

	let actions = [
		{
			tooltip: "Agregar",
			icon: "add",
			onClick: handleAgregar,
			isFreeAction: true,
		}, {
			tooltip: "Editar",
			icon: "edit",
			onClick: handleActualizar,
		}, {
			tooltip: "Eliminar",
			icon: "delete",
			onClick: handleEliminar,
		}
	];

	return (
		<Fragment>
			<MaterialTable
				title="Perfiles"
				is_loading={PerfilesSWR.isValidating}
				data={PerfilesSWR && PerfilesSWR.data?.data}
				columns={columns}
				actions={actions}
			/>
			<Dialog open={Boolean(formik.values.tipo)} maxWidth="md" fullWidth>
				<DialogTitle>
					{formik.values.tipo === "agregar" && "Agregar Perfil"}
					{formik.values.tipo === "actualizar" && "Actualizar Perfil"}
					{formik.values.tipo === "eliminar" && "Eliminar Perfil"}
				</DialogTitle>
				<DialogContent dividers>
					<DialogContentText>Formulario con información del Perfil.</DialogContentText>
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<TextField
								label="Nombre"
								name="nombre"
								value={formik.values.nombre}
								onChange={formik.handleChange}
								variant="outlined"
								fullWidth
								disabled={formik.values.tipo === "eliminar"}
							/>
						</Grid>
						<Grid item xs={6}>
							<TextField
								label="Descripción"
								name="descripcion"
								value={formik.values.descripcion}
								onChange={formik.handleChange}
								variant="outlined"
								fullWidth
								disabled={formik.values.tipo === "eliminar"}
							/>
						</Grid>
						<Grid item xs={12}>
							<Autocomplete
								multiple
								options={PermisosSWR ? PermisosSWR.data?.data : []}
								value={PermisosSWR && PermisosSWR.data?.data ? Array.from(PermisosSWR.data.data).filter(p => formik.values._permisos_ref.includes(p._id)) : []}
								getOptionLabel={(permiso) => permiso.nombre}
								disabled={formik.values.tipo === "eliminar"}
								onChange={(event, value) => {
									formik.setFieldValue("_permisos_ref", value.map(v => v._id));
								}}
								renderInput={(params) => (
									<TextField
										label="Permisos"
										variant="outlined"
										{...params}
									/>
								)}
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelar} variant="outlined" color="primary">
						Cancelar
					</Button>
					<Button onClick={formik.submitForm} variant="contained" color="primary">
						Aceptar
					</Button>
				</DialogActions>
			</Dialog>
		</Fragment>
	);
}